/**
 * @module features/devices/components/DeviceStatsCards
 * @description Fleet KPI cards and hardware category distribution.
 * Displays operational status metrics alongside subtype counts from stats.by_type.
 */
import * as React from "react";
import { Boxes, CheckCircle2, Cpu, PowerOff, Wrench } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { DeviceStats, DeviceTypeDescriptor } from "../types";
import { deviceTypeLabel } from "../utils/format";

interface DeviceStatsCardsProps {
  stats?: DeviceStats;
  types?: DeviceTypeDescriptor[];
  selectedType?: string;
  onTypeSelect?: (typeId: string) => void;
  isLoading?: boolean;
}

const CARD =
  "flex items-center gap-3 p-3.5 sm:p-4 h-[76px] rounded-2xl border border-border/70 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 shadow-xs";

export function DeviceStatsCards({
  stats,
  types,
  selectedType = "all",
  onTypeSelect,
  isLoading,
}: DeviceStatsCardsProps) {
  if (isLoading || !stats) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={CARD}>
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-10" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cards = [
    { label: "Total devices", value: stats.total_devices, icon: Boxes },
    { label: "Active", value: stats.by_status.ACTIVE ?? 0, icon: CheckCircle2 },
    { label: "Offline", value: stats.by_status.OFFLINE ?? 0, icon: PowerOff },
    { label: "Maintenance", value: stats.by_status.MAINTENANCE ?? 0, icon: Wrench },
  ];

  const byTypeEntries = Object.entries(stats.by_type || {});

  return (
    <div className="space-y-3">
      {/* 4 Primary Operational Status Cards */}
      <section
        aria-label="Fleet operational summary"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
      >
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} className={CARD}>
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-zinc-900 dark:bg-zinc-800 text-white dark:text-zinc-100 shrink-0 ring-1 ring-black/5 dark:ring-white/10">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] sm:text-xs font-medium text-muted-foreground truncate">
                {label}
              </p>
              <p className="text-lg sm:text-2xl font-bold tracking-tight text-foreground">
                {value}
              </p>
            </div>
          </div>
        ))}
      </section>

      {/* Hardware Subtype Breakdown from stats.by_type */}
      {byTypeEntries.length > 0 && (
        <section
          aria-label="Hardware type distribution"
          className="flex flex-wrap items-center gap-2 p-2.5 sm:p-3 rounded-2xl border border-border/70 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 shadow-xs"
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mr-1">
            <Cpu className="h-4 w-4 text-foreground/70" />
            <span className="uppercase tracking-wider text-[11px]">Count By Device Type:</span>
          </div>

          <button
            type="button"
            onClick={() => onTypeSelect?.("all")}
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-150",
              selectedType === "all"
                ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs font-semibold ring-1 ring-border/20"
                : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
            )}
          >
            <span>All Types</span>
            <span
              className={cn(
                "font-mono text-[11px] px-1.5 py-0.5 rounded-md font-semibold",
                selectedType === "all"
                  ? "bg-white/20 dark:bg-black/20 text-inherit"
                  : "bg-background/80 text-foreground"
              )}
            >
              {stats.total_devices}
            </span>
          </button>

          {byTypeEntries.map(([typeKey, count]) => {
            const matchedType = types?.find((t) => t.type_id === typeKey);
            const label = matchedType?.label ?? deviceTypeLabel(typeKey);
            const isSelected = selectedType === typeKey;

            return (
              <button
                key={typeKey}
                type="button"
                onClick={() => onTypeSelect?.(isSelected ? "all" : typeKey)}
                className={cn(
                  "inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-150 border",
                  isSelected
                    ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 border-transparent shadow-xs font-semibold ring-1 ring-border/20"
                    : "bg-card dark:bg-zinc-900 border-border/70 dark:border-zinc-800 text-foreground hover:bg-muted/70"
                )}
                title={`Filter by ${label} (${count} registered)`}
              >
                <span>{label}</span>
                <span
                  className={cn(
                    "font-mono text-[11px] px-1.5 py-0.5 rounded-md font-semibold",
                    isSelected
                      ? "bg-white/20 dark:bg-black/20 text-inherit"
                      : "bg-muted text-foreground"
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </section>
      )}
    </div>
  );
}
